import { useState, useEffect } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { CalendarIcon } from "lucide-react";
import api from "../lib/api";

const fmt = (d) => {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const parse = (s) => (s ? new Date(s + "T00:00:00") : undefined);

export default function DatePickerField({ value, onChange, testid = "reservation-date" }) {
  const [open, setOpen] = useState(false);
  const [overview, setOverview] = useState({}); // date -> status
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const loadOverview = (fromDate, toDate) => {
    api.get(`/availability/overview`, { params: { from_: fmt(fromDate), to: fmt(toDate) } })
      .then((r) => {
        const m = {};
        (r.data || []).forEach((it) => { m[it.date] = it.status; });
        setOverview(m);
      })
      .catch(() => {});
  };

  useEffect(() => {
    const from = new Date(today); from.setDate(1);
    const to = new Date(today); to.setMonth(to.getMonth() + 3);
    loadOverview(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusOf = (d) => overview[fmt(d)];

  const display = value
    ? new Date(value + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    : "Select a date";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2.5 text-left flex items-center justify-between transition-colors ${
            value ? "text-[#2D3748]" : "text-[#4A5568]/60"
          }`}
        >
          <span>{display}</span>
          <CalendarIcon size={16} className="text-[#5979A9]/70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <div className="px-3 pt-3 pb-2 flex items-center gap-3 text-[10px] tracking-wide text-[#4A5568]">
          <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500"/>Available</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-400"/>Limited</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#E05947]"/>Unavailable</span>
        </div>
        <Calendar
          mode="single"
          selected={parse(value)}
          onSelect={(d) => {
            if (!d) return;
            if (statusOf(d) === "closed") return;
            onChange(fmt(d));
            setOpen(false);
          }}
          onMonthChange={(month) => {
            const from = new Date(month);
            const to = new Date(month); to.setMonth(to.getMonth() + 2);
            loadOverview(from, to);
          }}
          disabled={(d) => d < today || statusOf(d) === "closed"}
          modifiers={{
            avail: (d) => d >= today && statusOf(d) === "available",
            partial: (d) => d >= today && statusOf(d) === "partial",
            closed: (d) => d >= today && statusOf(d) === "closed",
          }}
          modifiersClassNames={{
            avail: "ring-1 ring-green-500/40 text-green-700",
            partial: "ring-1 ring-yellow-500/60 text-yellow-700 bg-yellow-50",
            closed: "bg-[#E05947]/15 text-[#E05947] line-through !opacity-100",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
